<mat-card class="chart-card">
  <mat-card-header>
    <mat-card-title>{{ 'analytics.supplyDemandDashboard' | transloco }}</mat-card-title>
    <mat-card-subtitle>{{ 'analytics.comprehensiveView' | transloco }}</mat-card-subtitle>
  </mat-card-header>

  <mat-card-content>
    <!-- SSR placeholder -->
    <div *ngIf="!isBrowser" class="ssr-placeholder">
      <p>{{ 'analytics.loadingDashboard' | transloco }}</p>
    </div>

    <!-- Render dashboard only in browser -->
    <ng-container *ngIf="isBrowser">
      <!-- Loading state -->
      <div *ngIf="loading" class="loading-overlay">
        <mat-spinner diameter="50"></mat-spinner>
        <p>{{ 'analytics.loadingAnalytics' | transloco }}</p>
      </div>

      <!-- Error state -->
      <div *ngIf="error && !loading" class="error-message">
        <p>{{ error }}</p>
      </div>

      <!-- Dashboard Grid -->
      <div *ngIf="!loading && !error" class="dashboard-grid">
        <!-- Metric Cards - Full Width Top Row -->
        <div class="metrics-section">
          <div class="metric-card">
            <div class="metric-icon jobs">
              <i class="fas fa-briefcase"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">{{ 'analytics.availableJobs' | transloco }}</p>
              <h2 class="metric-value">{{ availableJobs }}</h2>
            </div>
          </div>

          <div class="metric-card">
            <div class="metric-icon candidates">
              <i class="fas fa-users"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">{{ 'analytics.availableCandidates' | transloco }}</p>
              <h2 class="metric-value">{{ availableCandidates }}</h2>
            </div>
          </div>

          <div class="metric-card ratio-card">
            <div class="metric-icon ratio-metric">
              <i class="fas fa-exchange-alt"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">{{ 'analytics.supplyDemandRatio' | transloco }}</p>
              <h2 class="metric-value">{{ supplyDemandRatio.toFixed(2) }}</h2>
              <span class="interpretation-badge" [ngClass]="getInterpretationClass(interpretation)">
                {{ getInterpretationText(interpretation) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Breakdown Tables - Flexible Layout -->
        <div class="breakdown-tables-container">
          <div class="breakdown-section table-section">
            <div class="chart-header">
              <h3>{{ 'analytics.byCategory' | transloco }}</h3>
              <p class="chart-subtitle">{{ 'analytics.topNinePlusOthers' | transloco }}</p>
            </div>
            <div class="table-container">
              <table *ngIf="categoryData.length > 0" class="breakdown-table">
                <thead>
                  <tr>
                    <th>{{ 'analytics.category' | transloco }}</th>
                    <th>{{ 'analytics.jobs' | transloco }}</th>
                    <th>{{ 'analytics.candidates' | transloco }}</th>
                    <th>{{ 'analytics.ratio' | transloco }}</th>
                    <th>{{ 'analytics.interpretation' | transloco }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of categoryData">
                    <td class="name-cell">{{ item.name }}</td>
                    <td>{{ item.jobs }}</td>
                    <td>{{ item.candidates }}</td>
                    <td class="ratio-cell">{{ item.ratio.toFixed(2) }}</td>
                    <td>
                      <span class="interpretation-badge" [ngClass]="getInterpretationClass(item.interpretation)">
                        {{ getInterpretationText(item.interpretation) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div *ngIf="categoryData.length === 0" class="no-data">
                <p>{{ 'analytics.noCategoryData' | transloco }}</p>
              </div>
            </div>
          </div>

          <div class="breakdown-section table-section">
            <div class="chart-header">
              <h3>{{ 'analytics.byPosition' | transloco }}</h3>
              <p class="chart-subtitle">{{ 'analytics.topNinePlusOthers' | transloco }}</p>
            </div>
            <div class="table-container">
              <table *ngIf="positionData.length > 0" class="breakdown-table">
                <thead>
                  <tr>
                    <th>{{ 'analytics.position' | transloco }}</th>
                    <th>{{ 'analytics.jobs' | transloco }}</th>
                    <th>{{ 'analytics.candidates' | transloco }}</th>
                    <th>{{ 'analytics.ratio' | transloco }}</th>
                    <th>{{ 'analytics.interpretation' | transloco }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of positionData">
                    <td class="name-cell">{{ item.name }}</td>
                    <td>{{ item.jobs }}</td>
                    <td>{{ item.candidates }}</td>
                    <td class="ratio-cell">{{ item.ratio.toFixed(2) }}</td>
                    <td>
                      <span class="interpretation-badge" [ngClass]="getInterpretationClass(item.interpretation)">
                        {{ getInterpretationText(item.interpretation) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div *ngIf="positionData.length === 0" class="no-data">
                <p>{{ 'analytics.noPositionData' | transloco }}</p>
              </div>
            </div>
          </div>

          <div class="breakdown-section table-section">
            <div class="chart-header">
              <h3>{{ 'analytics.byLocation' | transloco }}</h3>
              <p class="chart-subtitle">{{ 'analytics.topNinePlusOthers' | transloco }}</p>
            </div>
            <div class="table-container">
              <table *ngIf="locationData.length > 0" class="breakdown-table">
                <thead>
                  <tr>
                    <th>{{ 'analytics.location' | transloco }}</th>
                    <th>{{ 'analytics.jobs' | transloco }}</th>
                    <th>{{ 'analytics.candidates' | transloco }}</th>
                    <th>{{ 'analytics.ratio' | transloco }}</th>
                    <th>{{ 'analytics.interpretation' | transloco }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of locationData">
                    <td class="name-cell">{{ item.name }}</td>
                    <td>{{ item.jobs }}</td>
                    <td>{{ item.candidates }}</td>
                    <td class="ratio-cell">{{ item.ratio.toFixed(2) }}</td>
                    <td>
                      <span class="interpretation-badge" [ngClass]="getInterpretationClass(item.interpretation)">
                        {{ getInterpretationText(item.interpretation) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div *ngIf="locationData.length === 0" class="no-data">
                <p>{{ 'analytics.noLocationData' | transloco }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  </mat-card-content>
</mat-card>
